import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useBooking } from "../context/BookingContext";

export default function Navbar() {
  const { user, setUser } = useBooking();
  const location = useLocation();
  const [, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Hero is now white — navbar is always white
  const transparent = false;

  const navLinks = [
    { label: "Courts",     href: isHome ? "#courts"    : "/" },
    { label: "Gallery",    href: isHome ? "#gallery"   : "/" },
    { label: "Rate Chart", href: isHome ? "#pricing"   : "/" },
    { label: "Location",   href: isHome ? "#location"  : "/" },
  ];

  return (
    <motion.nav
      initial={{ y: -16, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        transparent
          ? "bg-transparent"
          : "bg-white/95 backdrop-blur-md shadow-sm border-b border-black/5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 group">
          <div className="w-8 h-8 rounded-lg bg-[#f59e0b] flex items-center justify-center flex-shrink-0">
            <span className="text-sm leading-none">🏏</span>
          </div>
          <span className="font-bold text-lg tracking-tight text-black">
            Town Square <span className="text-[#f59e0b]">Arena</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((l) => (
            <a
              key={l.label}
              href={l.href}
              className={`text-sm font-medium transition-colors ${
                transparent ? "text-white/80 hover:text-white" : "text-black/60 hover:text-black"
              }`}
            >
              {l.label}
            </a>
          ))}
          {user && (
            <>
              <Link
                to="/dashboard"
                className={`text-sm font-medium transition-colors ${transparent ? "text-white/80 hover:text-white" : "text-black/60 hover:text-black"}`}
              >
                My Bookings
              </Link>
              <button
                onClick={() => setUser(null)}
                className={`text-sm transition-colors ${transparent ? "text-white/50 hover:text-white" : "text-black/40 hover:text-black"}`}
              >
                Logout
              </button>
            </>
          )}
          <Link
            to="/book"
            className="bg-[#f59e0b] hover:bg-[#f59e0b]/90 text-black font-bold text-sm px-5 py-2.5 rounded-xl transition-all hover:shadow-lg hover:shadow-[#f59e0b]/25 hover:-translate-y-0.5"
          >
            Book a Slot
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          className={`md:hidden p-2 transition-colors ${transparent ? "text-white" : "text-black"}`}
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          <div className={`w-5 h-0.5 bg-current transition-all mb-1.5 ${menuOpen ? "rotate-45 translate-y-2" : ""}`} />
          <div className={`w-5 h-0.5 bg-current transition-all mb-1.5 ${menuOpen ? "opacity-0" : ""}`} />
          <div className={`w-5 h-0.5 bg-current transition-all ${menuOpen ? "-rotate-45 -translate-y-2" : ""}`} />
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-white border-t border-black/5 px-5 py-4 flex flex-col gap-1"
          >
            {navLinks.map((l) => (
              <a
                key={l.label}
                href={l.href}
                onClick={() => setMenuOpen(false)}
                className="text-sm font-medium text-black/70 hover:text-black py-3 border-b border-black/5"
              >
                {l.label}
              </a>
            ))}
            <div className="pt-3 flex flex-col gap-2">
              {user && (
                <>
                  <Link to="/dashboard" onClick={() => setMenuOpen(false)} className="text-sm text-black/60 py-2">My Bookings</Link>
                  <button onClick={() => { setUser(null); setMenuOpen(false); }} className="text-sm text-left text-black/40 py-2">Logout</button>
                </>
              )}
              <Link
                to="/book"
                onClick={() => setMenuOpen(false)}
                className="bg-[#f59e0b] text-black font-bold text-sm text-center px-5 py-3 rounded-xl"
              >
                Book a Slot
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
